import { z } from 'zod';
/**
 * FileRef — canonical reference to a file in a user codebase.
 *
 * Architecture plan §"File addressing": every reference to a
 * user-codebase artifact uses this triple. Two flavors:
 *
 *   1. Version-pinned: (repo_id, sha, path) — immutable identity,
 *      the form `code_commit_files` stores.
 *   2. Floating: (repo_id, branch, path) — resolves to a sha at
 *      read time via `CodeFactsRepository.resolveSha`.
 *
 * Both forms keep the same `path` and `repo_id`; what varies is
 * how the revision is selected. Discriminated by the presence of
 * `sha` vs `branch`.
 *
 * Prior art: git's own (repo, sha, path); GitHub URLs
 * (/{owner}/{repo}/blob/{ref}/{path}); GitHub Contents API ?ref=;
 * Sourcegraph (repository, revision, path). The shape is well-
 * established — we adopt it directly so the API and skills speak
 * the same language about files.
 *
 * We deliberately do NOT carry file content. Content fetch is on
 * the rare occasions we need it (GitHub Contents API for the
 * specific (repo, sha, path)); storage is git's job, not ours.
 */
export const FileRefPinned = z.object({
    repo_id: z.number().int().positive(),
    sha: z.string().min(4).max(40).regex(/^[0-9a-f]+$/i),
    path: z.string().min(1),
});
export const FileRefFloating = z.object({
    repo_id: z.number().int().positive(),
    branch: z.string().min(1).max(255),
    path: z.string().min(1),
});
/**
 * Discriminated union — either the pinned or floating form.
 * Consumers should accept FileRef and call resolveSha() at the
 * repository boundary to normalize to FileRefPinned when needed.
 */
export const FileRef = z.union([FileRefPinned, FileRefFloating]);
/**
 * Type guard for the pinned variant. Useful at repository
 * boundaries where the sha is required.
 */
export function isFileRefPinned(ref) {
    return 'sha' in ref;
}
//# sourceMappingURL=file-ref.js.map